// src/pages/Billing.jsx
// Analytics is limited by a monthly usage allowance (model cost), shown as a percentage — never
// dollars and never a run count. Free has a small allowance; Starter ($299/month) is self-serve;
// Team (from $1,500/month) is a conversation. `?plan=starter` (the analytics site's Starter
// button, via /start) highlights the Starter upgrade.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Paper, Stack, Typography, Divider, Button, LinearProgress, CircularProgress } from "@mui/material";
import { createCheckoutSession, getRunUsage } from "../utils/api";

const PLAN_NAMES = { free: "Free", starter: "Starter", pro: "Team", enterprise: "Enterprise" };
const TEAM_CONTACT = "https://assertion-ai.com/analytics#contact";

const monthDay = (iso) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric" });
  } catch (_) {
    return iso;
  }
};

function PlanCard({ label, price, lines, action, highlight }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5,
            borderColor: highlight ? "primary.main" : "divider", borderWidth: highlight ? 2 : 1 }}
    >
      <Typography variant="overline" color="primary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
      <Typography variant="h5" fontWeight={600}>{price}</Typography>
      <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.25, color: "text.secondary", fontSize: 14 }}>
        {lines.map((l) => <li key={l}>{l}</li>)}
      </Stack>
      <Box sx={{ mt: "auto", pt: 1 }}>{action}</Box>
    </Paper>
  );
}

export default function Billing() {
  const [params] = useSearchParams();
  const wantsStarter = params.get("plan") === "starter";
  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getRunUsage()
      .then(setUsage)
      .catch((e) => console.error("Failed to load usage:", e))
      .finally(() => setLoadingUsage(false));
  }, []);

  const tier = (usage?.tier || "free").toLowerCase();
  const allowance = usage?.allowance;
  const capped = allowance && allowance.allowance_usd != null;

  const upgradeToStarter = async () => {
    setCheckingOut(true);
    setError("");
    try {
      await createCheckoutSession(null, { plan: "starter", product: "analytics", returnTo: "/billing" });
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Could not start checkout. Try again.");
      setCheckingOut(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={700}>Billing & Plan</Typography>
        <Typography variant="paragraph" color="text.secondary">Your plan and this month's usage.</Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.paper" }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Usage this month</Typography>
        <Divider sx={{ mb: 2 }} />
        {loadingUsage ? (
          <Typography color="text.secondary">Loading usage…</Typography>
        ) : usage ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <Box sx={{ display: "inline-block", px: 2, py: 0.5, border: "2px solid", borderColor: "primary.main",
                       borderRadius: 1, color: "primary.main", fontWeight: 700, fontSize: "0.9rem",
                       textTransform: "uppercase", flexShrink: 0, alignSelf: "flex-start" }}>
              {PLAN_NAMES[tier] || tier} plan
            </Box>
            <Stack spacing={0.75} sx={{ flexGrow: 1, minWidth: 0 }}>
              {capped ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {allowance.exhausted
                      ? `This month's analytics allowance is used up. It resets on ${monthDay(allowance.resets_on)}.`
                      : `${allowance.left_pct}% of this month's analytics allowance left · resets ${monthDay(allowance.resets_on)}`}
                  </Typography>
                  <LinearProgress variant="determinate" value={allowance.used_pct ?? 0}
                                  color={allowance.exhausted ? "error" : "primary"} sx={{ height: 6, borderRadius: 5 }} />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">Your plan has no monthly usage cap.</Typography>
              )}
            </Stack>
          </Stack>
        ) : (
          <Typography color="error">Failed to load usage</Typography>
        )}
      </Paper>

      {!loadingUsage && (tier === "free" || tier === "starter") && (
        <Box>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            {tier === "free" ? "Upgrade" : "When your data outgrows spreadsheets"}
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {tier === "free" && (
              <PlanCard
                label="Starter"
                price="$299 / month"
                highlight={wantsStarter}
                lines={["A larger monthly usage allowance", "3 datasets, spreadsheet-sized", "Everyone on your team can use it", "Email support"]}
                action={
                  <Button variant="contained" fullWidth onClick={upgradeToStarter} disabled={checkingOut} sx={{ textTransform: "none" }}>
                    {checkingOut ? <CircularProgress size={20} color="inherit" /> : "Upgrade to Starter"}
                  </Button>
                }
              />
            )}
            <PlanCard
              label="Team"
              price="From $1,500 / month"
              lines={["Unlimited datasets, no size cap", "Connected directly to your data source", "Data prep and a question plan, with us", "A named contact and a shared Slack channel"]}
              action={
                <Button variant="outlined" fullWidth href={TEAM_CONTACT} target="_blank" rel="noopener" sx={{ textTransform: "none" }}>
                  Talk to us
                </Button>
              }
            />
          </Stack>
          {error && <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>{error}</Typography>}
        </Box>
      )}

      {!loadingUsage && tier !== "free" && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.paper" }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Manage your plan</Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" mb={2}>
            To change or cancel your plan, or to discuss annual billing, contact us.
          </Typography>
          <Button variant="outlined" onClick={() => (window.location.href = "mailto:support@assertion-ai.com")} sx={{ textTransform: "none" }}>
            Contact us
          </Button>
        </Paper>
      )}
    </Stack>
  );
}
